import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import './FeatureFlagsAdmin.css';

interface FlagDefinition {
  key: string;
  label: string;
  description: string;
  group: 'Pages' | 'Home Page Cards';
  icon: string;
}

const FLAG_DEFINITIONS: FlagDefinition[] = [
  // ── Pages ──
  {
    key: 'feature_page_price_history',
    label: 'Price History Page',
    description: 'Controls access to /price-history. When disabled, non-admins are redirected.',
    group: 'Pages',
    icon: '📊',
  },
  {
    key: 'feature_page_map',
    label: 'Interactive Map Page',
    description: 'Controls access to /map.',
    group: 'Pages',
    icon: '🗺️',
  },
  {
    key: 'feature_page_damage_calc',
    label: 'Damage Calculator Page',
    description: 'Controls access to /damage-calculator.',
    group: 'Pages',
    icon: '⚔️',
  },
  {
    key: 'feature_page_submit',
    label: 'Submit Data Page',
    description: 'Controls access to /submit-data.',
    group: 'Pages',
    icon: '📤',
  },
  {
    key: 'feature_page_discord_bot',
    label: 'Discord Bot Dashboard',
    description: 'Controls access to /dashboard.',
    group: 'Pages',
    icon: '🤖',
  },
  // ── Home Page Cards ──
  {
    key: 'feature_home_price_history',
    label: 'Price History Card',
    description: 'Shows or hides the Price History card on the home page.',
    group: 'Home Page Cards',
    icon: '📊',
  },
  {
    key: 'feature_home_map',
    label: 'Map Card',
    description: 'Shows or hides the Interactive Map card on the home page.',
    group: 'Home Page Cards',
    icon: '🗺️',
  },
  {
    key: 'feature_home_damage_calc',
    label: 'Damage Calculator Card',
    description: 'Shows or hides the Damage Calculator card on the home page.',
    group: 'Home Page Cards',
    icon: '⚔️',
  },
  {
    key: 'feature_home_discord',
    label: 'Discord Bot Card',
    description: 'Shows or hides the Discord Bot card on the home page.',
    group: 'Home Page Cards',
    icon: '🤖',
  },
  {
    key: 'feature_home_faq',
    label: 'FAQ Card',
    description: 'Shows or hides the FAQ card on the home page.',
    group: 'Home Page Cards',
    icon: '📖',
  },
];

const GROUPS: Array<FlagDefinition['group']> = ['Pages', 'Home Page Cards'];

const FeatureFlagsAdmin: React.FC = () => {
  const { flags, refresh: refreshGlobalFlags } = useFeatureFlags();

  // Local copy so we can show unsaved changes
  const [localFlags, setLocalFlags] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Initialise from global flags
    const initial: Record<string, boolean> = {};
    FLAG_DEFINITIONS.forEach((def) => {
      initial[def.key] = (flags as any)[def.key] ?? true;
    });
    setLocalFlags(initial);
  }, [flags]);

  const handleToggle = async (key: string) => {
    const newValue = !localFlags[key];

    // Optimistic update
    setLocalFlags((prev) => ({ ...prev, [key]: newValue }));
    setSaving((prev) => ({ ...prev, [key]: true }));
    setSaved((prev) => ({ ...prev, [key]: false }));
    setError(null);

    try {
      await adminAPI.updateSystemSetting(key, String(newValue));
      await refreshGlobalFlags();
      setSaved((prev) => ({ ...prev, [key]: true }));
      setTimeout(() => setSaved((prev) => ({ ...prev, [key]: false })), 2000);
    } catch (err) {
      // Revert on failure
      setLocalFlags((prev) => ({ ...prev, [key]: !newValue }));
      setError(`Failed to save "${key}". Please try again.`);
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  return (
    <div className="ffa-container">
      <div className="ffa-header">
        <h2>🚩 Feature Flags</h2>
        <p>
          Toggle pages and home-page cards on or off. Admins always retain access
          to every page regardless of these settings.
        </p>
      </div>

      {error && (
        <div className="ffa-error">
          ⚠️ {error}
        </div>
      )}

      {GROUPS.map((group) => {
        const defs = FLAG_DEFINITIONS.filter((d) => d.group === group);
        return (
          <section key={group} className="ffa-group">
            <h3 className="ffa-group-title">{group}</h3>
            <div className="ffa-cards">
              {defs.map((def) => {
                const enabled = localFlags[def.key] ?? true;
                const isSaving = saving[def.key];
                const wasSaved = saved[def.key];
                return (
                  <div
                    key={def.key}
                    className={`ffa-card ${enabled ? 'ffa-card--on' : 'ffa-card--off'}`}
                  >
                    <div className="ffa-card-left">
                      <span className="ffa-card-icon">{def.icon}</span>
                      <div className="ffa-card-info">
                        <div className="ffa-card-label">{def.label}</div>
                        <div className="ffa-card-desc">{def.description}</div>
                      </div>
                    </div>
                    <div className="ffa-card-right">
                      {isSaving && <span className="ffa-saving">Saving…</span>}
                      {wasSaved && !isSaving && <span className="ffa-saved">✓ Saved</span>}
                      <button
                        className={`ffa-toggle ${enabled ? 'ffa-toggle--on' : 'ffa-toggle--off'}`}
                        onClick={() => handleToggle(def.key)}
                        disabled={isSaving}
                        aria-label={`${enabled ? 'Disable' : 'Enable'} ${def.label}`}
                      >
                        <span className="ffa-toggle-knob" />
                      </button>
                      <span className={`ffa-status ${enabled ? 'ffa-status--on' : 'ffa-status--off'}`}>
                        {enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default FeatureFlagsAdmin;
